/**
 * Plaid Link, loaded only when it is about to open (C-19, migration 0076).
 *
 * The script comes from `https://cdn.plaid.com`, which `public/_headers`
 * names in `script-src` and `frame-src` for every page, since the policy is
 * one for the whole site. What keeps it from loading on every page is this:
 * nothing calls `openPlaidLink` until the consent step's Continue To Plaid is
 * pressed, so before then no Plaid script is in the page at all.
 *
 * A module of its own so a test replaces it: jsdom loads no scripts.
 */

export const PLAID_LINK_SCRIPT = 'https://cdn.plaid.com/link/v2/stable/link-initialize.js';

interface PlaidHandler {
  open(): void;
  destroy(): void;
}

interface PlaidGlobal {
  create(config: {
    token: string;
    onSuccess: (publicToken: string, metadata: { institution?: { name?: string } | null }) => void;
    onExit: (error: PlaidLinkExitError | null) => void;
  }): PlaidHandler;
}

declare global {
  interface Window {
    Plaid?: PlaidGlobal;
  }
}

/** What Link hands `onExit` when it closed because something failed. */
interface PlaidLinkExitError {
  error_code?: string;
  error_message?: string;
  display_message?: string | null;
}

/**
 * Link closed on an error, not because the person left (review PC-1). Its
 * message is a sentence for the screen. Callers recognise it by `name`, so a
 * test that replaces this module need not provide the class.
 */
export class PlaidLinkError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'PlaidLinkError';
  }
}

let loading: Promise<PlaidGlobal> | null = null;

function load(): Promise<PlaidGlobal> {
  if (window.Plaid) return Promise.resolve(window.Plaid);
  loading ??= new Promise<PlaidGlobal>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = PLAID_LINK_SCRIPT;
    script.async = true;
    script.onload = () => (window.Plaid ? resolve(window.Plaid) : reject(new Error('Plaid Link did not load.')));
    script.onerror = () => {
      loading = null;
      reject(new Error('Plaid Link could not be loaded.'));
    };
    document.head.appendChild(script);
  });
  return loading;
}

export interface LinkResult {
  publicToken: string;
  institutionName: string | null;
}

/**
 * Opens Link with the token the API made, and settles when it closes: with
 * the public token, null when the person leaves without connecting, or a
 * `PlaidLinkError` when Link closed because something failed (review PC-1).
 */
export async function openPlaidLink(linkToken: string): Promise<LinkResult | null> {
  const plaid = await load();
  return new Promise<LinkResult | null>((resolve, reject) => {
    const handler = plaid.create({
      token: linkToken,
      onSuccess: (publicToken, metadata) => {
        handler.destroy();
        resolve({ publicToken, institutionName: metadata.institution?.name ?? null });
      },
      onExit: (error) => {
        handler.destroy();
        if (error) {
          const said = error.display_message || error.error_message || 'Plaid stopped with an error';
          reject(new PlaidLinkError(`${said.replace(/\.$/, '')}${error.error_code ? ` (${error.error_code})` : ''}.`));
          return;
        }
        resolve(null);
      },
    });
    handler.open();
  });
}
